import { zodResolver } from "@hookform/resolvers/zod";
import type { UserAdminDetailDto } from "Infrastructure/Api/Api";
import { useForm } from "react-hook-form";

import { useUpdateUserMutation } from "Api/Mutations/User/useUpdateUserMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledForm } from "Components/Users/UserDetailPageContent/Components/StyledForm";
import { UserFormFields } from "Components/Users/UserDetailPageContent/Components/UserFormFields";
import { ProgramParticipationSection } from "Components/Users/UserDetailPageContent/Components/ProgramParticipationSection";
import { getUserValidationSchema } from "Components/Users/schemas";
import { isAccessRoleCode } from "Models/AccessRoleCodes";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";
import {
  getIBANFromLocalBankAccountNumber,
  getLocalBankAccountNumberFromIBAN,
} from "Utils/AccountUtils";
import type { z } from "zod";

type UserFormProps = {
  user: UserAdminDetailDto;
};

export type UserValidationType = z.infer<
  ReturnType<typeof getUserValidationSchema>
>;

export const UserForm = ({ user }: UserFormProps) => {
  const { t } = useResource();
  const {
    handleSubmit,
    control: userControl,
    formState: { isDirty },
    reset,
    setError,
  } = useForm<UserValidationType>({
    resolver: zodResolver(getUserValidationSchema(t)),
    defaultValues: {
      ...user,
      bankAccountNumber: user.verifiedIBAN
        ? getLocalBankAccountNumberFromIBAN(user.verifiedIBAN)
        : undefined,
    },
  });

  const { mutate: updateUser, isPending } = useUpdateUserMutation({
    onSuccess: (data) => {
      reset(data);
      toast.success(
        t(Resources.Pages.Content.UserDetailPageContent.SaveSuccess),
      );
    },
    // biome-ignore lint/suspicious/noExplicitAny: The error types are a mess and types generated in api.ts are wrong
    onError: (e: any) => {
      if (e?.data?.errors?.Login && e?.status === 409) {
        setError("login", {
          message: t(
            Resources.Pages.Content.UserDetailPageContent.DuplicateLoginError,
          ),
        });
      }
      toast.error(t(Resources.Pages.Content.UserDetailPageContent.SaveError));
    },
  });

  const onSubmit = (data: UserValidationType) => {
    const verifiedIBAN = data.bankAccountNumber
      ? getIBANFromLocalBankAccountNumber(data.bankAccountNumber, "CZ")
      : undefined;

    if (
      (data.bankAccountNumber || data.bankVerificationSource) &&
      !verifiedIBAN
    ) {
      setError("bankAccountNumber", {
        message: t(Resources.Validation.InvalidBankAccount),
      });
      return;
    }

    updateUser({
      ...data,
      accessRoleCode: user.accessRoleCode,
      // Read-only pass-through — the update endpoint ignores identity role grants.
      identityRoles: user.identityRoles,
      verifiedIBAN,
    });
  };

  const userType = isAccessRoleCode(user.accessRoleCode)
    ? user.accessRoleCode
    : undefined;

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <PrimaryPaper>
        <UserFormFields
          control={userControl}
          userType={userType}
          isBankVerificationDisabled={
            !!user.bankVerificationSource && !!user.verifiedIBAN
          }
        />
        <ProgramParticipationSection
          participation={user.stardustParticipation}
        />
        <ProgramParticipationSection
          participation={user.thanksToYouParticipation}
        />
        <PrimaryButton
          fullWidth
          type="submit"
          disabled={!isDirty}
          isLoading={isPending}
          variant="contained"
          color="primary"
        >
          {t(Resources.Common.Save)}
        </PrimaryButton>
      </PrimaryPaper>
    </StyledForm>
  );
};
